import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Database, Layers, List, LogOut, Settings, Share2, FolderOpen } from 'lucide-react';

export default function Navbar() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  const isActive = (path) => {
    if (path === '/schemas') return location.pathname === '/' || location.pathname === '/schemas' || location.pathname.startsWith('/schemas/');
    if (path === '/attributes') return location.pathname.startsWith('/attributes/');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    { path: '/schema-groups', label: 'Groups', icon: FolderOpen },
    { path: '/schemas', label: 'Schemas', icon: Database },
    { path: '/entities', label: 'Entities', icon: Layers },
    { path: '/valuesets', label: 'Valuesets', icon: List },
  ];
  if (hasPermission('create_proposals')) {
    navItems.push({ path: '/proposals', label: 'Proposals', icon: Share2 });
  }

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]" data-testid="navbar">
      <div className="flex items-center h-14 px-6 gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity" data-testid="nav-logo">
          <img src="/triomics-logo.png" alt="Triomics" className="h-5" />
          <span className="text-xs font-semibold text-[#9CA3AF] tracking-wider uppercase">Data Dictionary</span>
        </Link>

        <nav className="flex items-center gap-1 ml-2" data-testid="nav-links">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase()}-link`}
              className={`px-3 py-1.5 text-xs font-semibold tracking-wide uppercase transition-colors flex items-center gap-1.5 ${
                isActive(path)
                  ? 'text-[#002FA7] bg-[#002FA7]/5'
                  : 'text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F9FAFB]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {label}
            </Link>
          ))}
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className={`px-3 py-1.5 text-xs font-semibold tracking-wide uppercase transition-colors flex items-center gap-1.5 ${
                location.pathname === '/admin'
                  ? 'text-[#002FA7] bg-[#002FA7]/5'
                  : 'text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F9FAFB]'
              }`}
            >
              <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
              Admin
            </Link>
          )}
        </nav>

        <div className="flex-1" />

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 cursor-pointer outline-none" data-testid="user-menu-trigger">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="text-[10px] font-semibold bg-zinc-100 text-zinc-600">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium text-[#4B5563] hidden sm:block">{user.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-none">
              <div className="px-2 py-1.5">
                <p className="text-xs font-medium text-[#0A0A0A]">{user.name}</p>
                <p className="text-[10px] text-[#9CA3AF]">{user.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600 focus:text-red-600">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
